/**
 * Viewer credentials from forint, and the tiler calls the map makes with them.
 *
 * Forint never proxies bytes: the browser talks to the tiler for raster tiles and to the bucket
 * for photographs and vector files, carrying the short-lived credential minted here.
 */
import { api, unwrap } from './client';
import type { components } from './schema';
import type { SpectralMeta } from '@/map/spectral';

export type ViewerToken = components['schemas']['ViewerToken'];
export type GrantedObject = components['schemas']['GrantedObject'];

/** A token for the objects a project page wants to draw. */
export async function projectViewerToken(projectId: string, objects: string[]): Promise<ViewerToken> {
  return unwrap(
    await api.POST('/api/projects/{public_id}/viewer-token', { params: { path: { public_id: projectId } }, body: { objects } }),
  );
}

/** A token for every input of a report, for a signed-in viewer. */
export async function reportViewerToken(reportId: string): Promise<ViewerToken> {
  return unwrap(await api.GET('/api/reports/{public_id}/viewer-token', { params: { path: { public_id: reportId } } }));
}

/** A token for the holder of a report's secret link; no session needed. */
export async function publicReportViewerToken(secret: string): Promise<ViewerToken> {
  return unwrap(await api.POST('/api/r/{secret}/viewer-token', { params: { path: { secret } } }));
}

export type ViewerLink = components['schemas']['ViewerLink'];

/** A signed bucket link to one file of an object, for a signed-in viewer. */
export async function projectViewerLink(projectId: string, object: string, path: string): Promise<ViewerLink> {
  return unwrap(
    await api.POST('/api/projects/{public_id}/viewer-links', { params: { path: { public_id: projectId } }, body: { object, path } }),
  );
}

/** A signed link to one file of an input a report binds; the report decides, not delivery (RP14). */
export async function reportViewerLink(reportId: string, object: string, path: string): Promise<ViewerLink> {
  return unwrap(
    await api.POST('/api/reports/{public_id}/viewer-links', { params: { path: { public_id: reportId } }, body: { object, path } }),
  );
}

/** The same, for the holder of a report's secret link. */
export async function publicReportViewerLink(secret: string, object: string, path: string): Promise<ViewerLink> {
  return unwrap(await api.POST('/api/r/{secret}/viewer-links', { params: { path: { secret } }, body: { object, path } }));
}

/** What the tiler says about a raster (docs/tiler-api.md §2.2). */
export interface RasterMeta {
  kind: 'raster';
  bounds: [number, number, number, number];
  crs: string;
  resolution_m: number;
  minzoom: number;
  maxzoom: number;
  bands: number;
  dtype: string;
  nodata: number | null;
  value_range: [number, number][];
  warnings: string[];
  /** The file's per-band scale and offset; `unscaled` when the tiler reads through them (ranges and stretches in reflectance). */
  scales?: number[];
  offsets?: number[];
  unscaled?: boolean;
  /** What each band is called in the file, and the multispectral bands and indices found by those names. */
  band_names?: SpectralMeta['band_names'];
  band_roles?: SpectralMeta['band_roles'];
  indices?: SpectralMeta['indices'];
  index_ranges?: SpectralMeta['index_ranges'];
  /** Whether the tiles come from the raster's `.web.tif` display copy; the rest of the document is always the original's. */
  web_copy?: boolean;
}

function tilerUrl(baseUrl: string, path: string, params: Record<string, string>): string {
  const query = new URLSearchParams(params).toString();
  return `${baseUrl}${path}${query ? `?${query}` : ''}`;
}

export function rasterMetaUrl(baseUrl: string, object: string, digest: string, token: string): string {
  return tilerUrl(baseUrl, `/v1/objects/${object}/meta`, { d: digest, token });
}

export async function fetchRasterMeta(baseUrl: string, object: string, digest: string, token: string): Promise<RasterMeta> {
  const response = await fetch(rasterMetaUrl(baseUrl, object, digest, token));
  if (!response.ok) throw new Error(`tiler ${response.status}`);
  return (await response.json()) as RasterMeta;
}
